import CloseIcon from '@mui/icons-material/Close';
import { ButtonBase, Dialog, DialogContent, IconButton, Stack, Typography } from '@mui/material';
import { ProductGroups } from 'enums';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { CampaignTemplate, FlowCreationTabs } from '../../campaign-builder.types';
import AddedTemplateSuccessDialog from './addedTemplateSuccessDialog';
import FromScratch from './from-scratch';
import TemplateList from './template-list';

interface Props {
  open: boolean;
  loading: boolean;

  productGroup: ProductGroups;

  activeTab: FlowCreationTabs;
  templates: CampaignTemplate[];
  openTemplateSuccessDialog: boolean;

  handleSelectActiveTab(newActive: FlowCreationTabs): () => void;
  handleClose(): void;
  handleApplyTemplate(templateKey: string): () => void;
  handleApplyEmptyTemplate(): void;
  handleCloseTemplateSuccessDialog(): void;
}

const FlowCreationDialog: React.FC<Props> = ({
  open,
  loading,
  productGroup,
  activeTab,
  templates,
  handleSelectActiveTab,
  handleClose,
  handleApplyTemplate,
  handleApplyEmptyTemplate,
  openTemplateSuccessDialog,
  handleCloseTemplateSuccessDialog,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const getTabContent = () => {
    switch (activeTab) {
      case FlowCreationTabs.FROM_TEMPLATE:
        return (
          <TemplateList
            templates={templates}
            loading={loading}
            handleApplyTemplate={handleApplyTemplate}
          />
        );
      case FlowCreationTabs.FROM_SCRATCH:
        return <FromScratch loading={loading} handleApplyEmptyTemplate={handleApplyEmptyTemplate} />;
      case FlowCreationTabs.FROM_EXISTING_CAMPAIGN:
        return <>From Existing Campaign</>;
      default:
        return <></>;
    }
  };

  return (
    <>
      <Dialog open={open} classes={{ paper: classes.paper, container: classes.container }} fullWidth maxWidth="md">
        <DialogContent className={classes.content}>
          <IconButton className={classes.iconButton} size="small" onClick={handleClose}>
            <CloseIcon className={classes.icon} />
          </IconButton>
          <Stack className={classes.innerContainer} gap={3}>
            <div>
              <Typography className={classes.titleText}>{t('startHereTitle')}</Typography>
              <Typography className={classes.subTitleText}>{t('startHereSubtitle')}</Typography>
            </div>
            <div className={classes.tabContainer}>
              {/* TABS */}
              <Stack className={classes.tabs} direction={'row'}>
                <ButtonBase
                  className={activeTab === FlowCreationTabs.FROM_TEMPLATE ? classes.tabActive : classes.tab}
                  onClick={handleSelectActiveTab(FlowCreationTabs.FROM_TEMPLATE)}
                >
                  {t('useTemplate')}
                </ButtonBase>
                <ButtonBase
                  className={activeTab === FlowCreationTabs.FROM_SCRATCH ? classes.tabActive : classes.tab}
                  onClick={handleSelectActiveTab(FlowCreationTabs.FROM_SCRATCH)}
                >
                  {t('setupFromScratch')}
                </ButtonBase>
              </Stack>

              <div className={classes.tabContent}>{getTabContent()}</div>
            </div>
          </Stack>
        </DialogContent>
      </Dialog>

      <AddedTemplateSuccessDialog productGroup={productGroup} open={openTemplateSuccessDialog} handleClose={handleCloseTemplateSuccessDialog} />
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    borderRadius: '16px',
  },
  container: {
    maxHeight: '960px',
  },
  content: {
    padding: '32px 24px 24px 24px !important',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  iconButton: {
    position: 'absolute',
    right: '12px',
    top: '12px',
  },
  icon: {
    fontSize: theme.typography.pxToRem(24),
    color: theme.palette.greys.medium,
  },
  innerContainer: {
    overflow: 'hidden',
  },
  titleText: {
    fontSize: theme.typography.pxToRem(30),
    fontWeight: 'bold',
    color: theme.palette.primary.main,
    textAlign: 'center',
  },
  subTitleText: {
    fontSize: theme.typography.pxToRem(18),
    textAlign: 'center',
  },
  tabContainer: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  tabs: {
    borderTopLeftRadius: '10px',
    borderTopRightRadius: '10px',
  },
  tab: {
    borderTopLeftRadius: '10px',
    borderTopRightRadius: '10px',
    flex: 1,
    color: theme.palette.greys.medium,
    padding: '12px',
    fontSize: theme.typography.pxToRem(15),
    textAlign: 'center',
    '&:hover': {
      background: theme.palette.backgrounds.main_new,
    },
  },
  tabActive: {
    borderTopLeftRadius: '10px',
    borderTopRightRadius: '10px',
    flex: 1,
    color: theme.palette.primary.main,
    background: theme.palette.backgrounds.main_new,
    padding: '12px',
    fontSize: theme.typography.pxToRem(15),
    textAlign: 'center',
  },
  tabContent: {
    borderBottomLeftRadius: '10px',
    borderBottomRightRadius: '10px',
    background: theme.palette.backgrounds.main_new,
    padding: '24px',
    overflowY: 'auto',
  },
}));

export default FlowCreationDialog;
